// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { type InternalProposalOperation } from '@shared/data-access/interfaces/api/brokerage/proposals-types';
import { selectClientId } from '@shared/data-access/slices/clients/clients-selectors';
import { useModal } from '@shared/ui-kit';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  proposalFormSchema,
  type ProposalFormData,
} from '@shared/data-access/schemas/proposals';
import { useTranslation } from 'react-i18next';
import { useProposalVolatilityValidation } from './use-proposal-volatility-validation';
import { useSentProposal } from '@shared/advisory/proposals/hooks/use-sent-proposal';
import { selectAccountCurrency } from '@shared/data-access/slices/user/user-selectors';
import { advisoryApi } from '@shared/data-access/api/brokerage/advisory-api';

export const useProposalModal = ({
  operations = [],
  validProposal = true,
}: {
  operations?: InternalProposalOperation[];
  validProposal?: boolean;
}) => {
  const { t } = useTranslation();

  const clientId = useSelector(selectClientId);
  const currency = useSelector(selectAccountCurrency);

  const { data: profileData, ...clientProfileQuery } =
    advisoryApi.useGetClientProfileQuery(
      {
        client_id: clientId!,
      },
      {
        skip: clientId === null,
      },
    );

  const {
    volatilityExceedsBenchmark,
    isLoading: isLoadingVolatilityValidation,
    benchmark,
    advisoryProposalAccounts,
    isSimulationSuccessful,
  } = useProposalVolatilityValidation({
    operations,
    advisory_proposal: profileData?.advisory_proposal,
  });

  const { onSendProposal: handleSendProposal, isLoading } = useSentProposal();

  const isValidatingProposal = useMemo(() => {
    return [clientProfileQuery.isLoading, isLoadingVolatilityValidation].some(
      Boolean,
    );
  }, [clientProfileQuery.isLoading, isLoadingVolatilityValidation]);

  const form = useForm<ProposalFormData>({
    resolver: zodResolver(proposalFormSchema),
    defaultValues: {
      name: '',
      reasons: [],
      otherReason: undefined,
    },
  });

  const [Modal, modal] = useModal({
    component: 'dialog',
    defaultOpen: false,
    beforeOpen: useCallback(
      /* istanbul ignore next */ () => {
        form.reset({
          name: '',
          reasons: [],
          otherReason: undefined,
        });
      },
      [form],
    ),
  });

  const isDisabled =
    !clientId || !form.formState.isValid || isLoadingVolatilityValidation;

  const canSendProposal = useMemo(() => {
    return (
      operations.length > 0 &&
      benchmark.length > 0 &&
      profileData?.advisory_proposal?.status === 'active' &&
      validProposal &&
      !isValidatingProposal &&
      isSimulationSuccessful
    );
  }, [
    operations.length,
    benchmark.length,
    profileData?.advisory_proposal?.status,
    validProposal,
    isValidatingProposal,
    isSimulationSuccessful,
  ]);

  const onSendProposal: SubmitHandler<ProposalFormData> = useCallback(
    data => {
      const reasonsArray = t('proposal-modal.reasons', {
        ns: 'components',
        returnObjects: true,
      }) as unknown as string[];

      const description = data.reasons
        .map((index, listIndex) =>
          index === 9 && data.otherReason
            ? `${listIndex + 1}. ${t('proposal-modal.reason-other', {
                ns: 'components',
              })} ${data.otherReason}`
            : `${listIndex + 1}. ${reasonsArray[index]}`,
        )
        .join('\n');

      handleSendProposal({
        clientId: clientId!,
        riskProfile: 'moderate',
        name: data.name,
        description: description,
        operations: operations,
        currency: currency,
        advisoryProposalAccounts,
      });
    },
    [
      advisoryProposalAccounts,
      clientId,
      currency,
      handleSendProposal,
      operations,
      t,
    ],
  );

  return {
    Modal,
    modal,
    isDisabled,
    onSendProposal,
    isValidatingProposal,
    isLoading,
    form,
    t,
    clientId,
    operations,
    volatilityExceedsBenchmark,
    canSendProposal,
    isSimulationSuccessful,
    validProposal,
  };
};
